/** PUBLIC — email confirmation page. Opening it changes nothing; only the button (explicit POST) confirms.
 *  Routes: /customer-onboarding/verify/:token and /privacy-request/verify/:token */
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { BrandLogo } from "@/components/brand/BrandLogo";
import { CheckCircle2, MailCheck } from "lucide-react";

const CFG = {
  onboarding: { api: "/api/public/customer-onboarding/verify", title: "Confirm your email", button: "Confirm my email",
    done: "Thank you — your email address is confirmed and your details are with our team. We will be in touch. Confirming proves you control this mailbox; it does not verify your company's legal identity or GST registration." },
  privacy: { api: "/api/public/privacy-requests/verify", title: "Confirm your privacy request", button: "Confirm request",
    done: "Thank you — your request is confirmed and has been passed to our privacy team. We may contact you to confirm your identity before acting on it." },
};

export default function EmailConfirmPublic({ kind = "onboarding" }) {
  const { token } = useParams();
  const c = CFG[kind];
  const url = `${process.env.REACT_APP_BACKEND_URL}${c.api}/${token}`;
  const [v, setV] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { axios.get(url).then((r) => setV(r.data)).catch((e) => setErr(e.response?.data?.detail || "This link is not valid.")); }, [url]);
  const confirm = async () => {
    setBusy(true);
    try { await axios.post(url); setV({ ...v, state: "VERIFIED" }); } catch (e) { setErr(e.response?.data?.detail || "Could not confirm. Please try again."); }
    setBusy(false);
  };
  return (
    <div className="min-h-screen bg-[#f6f7f9] px-4 py-12" data-testid={`confirm-${kind}-page`}>
      <div className="mx-auto max-w-lg space-y-6">
        <BrandLogo alt="Suntronix" className="w-[160px]" style={{ mixBlendMode: "multiply" }} />
        <div className="rounded-2xl border border-slate-200 bg-white p-6 sm:p-8 space-y-4">
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-slate-900 inline-flex items-center gap-3"><MailCheck className="h-7 w-7 text-sky-700" />{c.title}</h1>
          {err && <p role="alert" className="text-sm text-rose-700" data-testid="confirm-error">{String(err)}</p>}
          {v && v.state === "PENDING" && <>
            <p className="text-sm text-slate-700">{kind === "onboarding" ? <>Organisation: <b>{v.organisation}</b><br /></> : null}Email: <b>{v.email}</b></p>
            <button disabled={busy} onClick={confirm} className="h-11 w-full rounded-full bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 disabled:opacity-50 transition-colors" data-testid="confirm-button">{busy ? "Confirming…" : c.button}</button>
          </>}
          {v && v.state === "VERIFIED" && <p role="status" className="flex gap-2 text-sm text-emerald-800" data-testid="confirm-done"><CheckCircle2 className="h-5 w-5 shrink-0" />{c.done}</p>}
          {v && (v.state === "EXPIRED" || v.state === "REVOKED") && <p className="text-sm text-amber-800" data-testid="confirm-expired">This link is no longer valid. Please submit the form again to receive a new link.</p>}
        </div>
      </div>
    </div>
  );
}
